/**
 * FurrBites — cart drawer enhancements.
 *
 * Adds two behaviours on top of Shrine's native cart drawer:
 *   1. One-time vs Subscribe & save  → PUT /cart/change.js with a real selling_plan id
 *   2. Shipping-protection opt-in    → POST /cart/add.js  /  /cart/change.js
 *
 * Everything is event-delegated on `document`, so it keeps working after Shrine
 * re-renders the drawer. Refreshing is done by diffing the freshly rendered
 * section against the live DOM child-by-child, deliberately skipping the
 * countdown timer so its running clock is never reset.
 */
(function () {
  'use strict';

  var SECTION_ID = 'cart-drawer';
  var TIMER_SELECTOR = '.cart-timer, countdown-timer';

  function root() {
    return (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  }

  function cartUrl(name) {
    var routes = window.routes || {};
    return (routes['cart_' + name + '_url'] || '/cart/' + name) + '.js';
  }

  function postJSON(url, body) {
    return fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body)
    }).then(function (response) {
      return response.json().then(function (data) {
        if (!response.ok) throw new Error(data.description || data.message || 'Cart update failed');
        return data;
      });
    });
  }

  /** True when this node is, or contains, the countdown timer. */
  function holdsTimer(el) {
    return el.matches(TIMER_SELECTOR) || !!el.querySelector(TIMER_SELECTOR);
  }

  /**
   * Replace stale children with freshly rendered ones, in place.
   * Untouched: the timer, and anything that did not actually change (so native
   * custom elements such as upsells only re-initialise when they need to).
   */
  function syncChildren(current, next) {
    if (!current || !next) return;
    var live = Array.prototype.slice.call(current.children);
    var fresh = Array.prototype.slice.call(next.children);
    if (live.length !== fresh.length) return;

    live.forEach(function (el, i) {
      if (holdsTimer(el)) return;
      if (el.isEqualNode(fresh[i])) return;
      el.replaceWith(fresh[i]);
    });
  }

  function refreshDrawer() {
    return fetch(root() + '?section_id=' + SECTION_ID)
      .then(function (r) { return r.text(); })
      .then(function (html) {
        var doc = new DOMParser().parseFromString(html, 'text/html');

        syncChildren(document.querySelector('.cart-drawer__body'), doc.querySelector('.cart-drawer__body'));
        syncChildren(document.querySelector('.drawer__footer'), doc.querySelector('.drawer__footer'));

        var liveHeading = document.querySelector('.drawer__heading');
        var freshHeading = doc.querySelector('.drawer__heading');
        if (liveHeading && freshHeading) liveHeading.innerHTML = freshHeading.innerHTML;

        return fetch(root() + '?section_id=cart-icon-bubble');
      })
      .then(function (r) { return r.text(); })
      .then(function (html) {
        var bubble = document.getElementById('cart-icon-bubble');
        if (!bubble) return;
        var fresh = new DOMParser().parseFromString(html, 'text/html').getElementById('cart-icon-bubble');
        if (fresh) bubble.innerHTML = fresh.innerHTML;
      })
      .catch(function (error) {
        /* Leave the drawer as-is; the cart itself already changed server-side. */
        console.error('[fb-cart] refresh failed', error);
      });
  }

  /* ----------------------------------------------------------------------- */
  /* 1. Subscription selector                                                */
  /* ----------------------------------------------------------------------- */

  function onPlanChange(input) {
    var group = input.closest('[data-fb-plan]');
    if (!group || group.hasAttribute('data-busy')) return;
    group.setAttribute('data-busy', '');

    var planId = input.value;
    postJSON(cartUrl('change'), {
      line: parseInt(group.dataset.line, 10),
      quantity: parseInt(group.dataset.quantity, 10) || 1,
      selling_plan: planId === '' ? null : parseInt(planId, 10)
    })
      .then(refreshDrawer)
      .catch(function (error) {
        console.error('[fb-cart] selling plan change failed', error);
        group.removeAttribute('data-busy');
      });
  }

  /* ----------------------------------------------------------------------- */
  /* 2. Shipping protection                                                  */
  /* ----------------------------------------------------------------------- */

  function onProtectionToggle(input) {
    var wrapper = input.closest('[data-fb-protect]');
    if (!wrapper || wrapper.hasAttribute('data-busy')) return;
    wrapper.setAttribute('data-busy', '');

    var request = input.checked
      ? postJSON(cartUrl('add'), { items: [{ id: parseInt(wrapper.dataset.variantId, 10), quantity: 1 }] })
      : postJSON(cartUrl('change'), { line: parseInt(wrapper.dataset.line, 10), quantity: 0 });

    request
      .then(refreshDrawer)
      .catch(function (error) {
        console.error('[fb-cart] shipping protection toggle failed', error);
        input.checked = !input.checked;
        wrapper.removeAttribute('data-busy');
      });
  }

  document.addEventListener('change', function (event) {
    var target = event.target;
    if (!(target instanceof HTMLInputElement)) return;
    if (target.matches('[data-fb-plan-input]')) onPlanChange(target);
    else if (target.matches('[data-fb-protect-input]')) onProtectionToggle(target);
  });
})();
